import { Component, computed, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs/operators'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { AppPaths } from 'src/app/app-routing.module'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { RoadSpeedSegmentPropertiesModel } from 'src/app/shared/models/road-speed-segment-properties.model'
import { RoadCoverageProperties } from 'src/app/shared/models/road-coverage.model'

@Component({
  selector: 'app-road-coverage-segment-details',
  templateUrl: './road-coverage-segment-details.component.html',
  standalone: false,
})
export class RoadCoverageSegmentDetailsComponent {
  public readonly details = signal(new Map<string, DetailsModel>())
  public readonly rawFeature = computed(() =>
    JSON.stringify(this.features()[this.index()], undefined, 2),
  )
  public readonly features = signal<MapGeoJSONFeature[]>([])
  public readonly zoomLevel = signal<number | undefined>(undefined)
  public readonly index = signal(0)
  public readonly selectedFeatureId = signal('')

  constructor(
    private readonly mapEventService: MapEventsService,
    public appState: AppStateService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        this.clearDetails()
        if (this.features().length != features.length) {
          this.index.set(0)
        }

        if (features[this.index()].layer.id === `${Layers.RoadCoverage.name}-lines`) {
          this.features.set(features)
          this.setDetails()
        }
      })
  }

  toggleFeature(index: number) {
    this.index.set(index)
    this.setDetails()
  }

  setDetails() {
    this.selectedFeatureId.set(
      this.features()[this.index()].properties?.['way_id']?.toString() ?? '',
    )
    const properties: RoadCoverageProperties = this.features()[this.index()]
      .properties as RoadCoverageProperties
    this.mapMetaToDetails(properties)
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: this.selectedFeatureId(),
    })
  }

  private mapMetaToDetails(properties: RoadCoverageProperties) {
    this.setDetail('Meta', {
      title: 'Road Segment',
      items: [
        {
          description: 'way_id',
          value: properties.way_id.toString(),
        },
        {
          description: 'direction',
          value: properties.direction,
        },
        {
          description: 'update_time',
          value: properties.update_time?.toString() ?? '',
        },
        {
          description: 'coverage',
          value: properties.coverage.toString(),
        },
      ],
    })
  }

  close() {
    this.clearDetails()
    this.appState.setState(AppPaths.Internal, {
      selectedFeatureId: undefined,
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this.details.update((details) => new Map(details).set(key, value))
  }

  private clearDetails(): void {
    this.details.set(new Map())
  }
}
